import { useMemo, useState, Fragment } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  PipelineLoan,
  PipelineStatus,
  DateRange,
  DatePreset,
  getBorrowerName,
  getLoanOfficerName
} from '../types';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { format, parseISO, startOfMonth } from 'date-fns';
import { formatBusinessDateTimeShort } from '@/utils/dateHelpers';
import { buildPipelineLineModel, STATUS_LINE_CONFIG } from './pipelineLineModel';

interface PipelineLineReportProps {
  loans: PipelineLoan[];
  isLoading: boolean;
  dateRange?: DateRange;
  datePreset?: DatePreset;
  selectedStatus?: PipelineStatus | 'all';
  onStatusChange?: (status: PipelineStatus | 'all') => void;
  // Drill from a nonzero status-count cell into the Activity tab, seeded with
  // that status (over the same report date range).
  onDrillToActivity?: (seed: { status: PipelineStatus }) => void;
}

// Format currency for display
function formatCurrency(amount: number): string {
  if (amount >= 1000000) {
    return `$${(amount / 1000000).toFixed(1)}M`;
  }
  if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(0)}K`;
  }
  return `$${amount.toFixed(0)}`;
}

function formatEventDate(value: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? format(parseISO(`${value}T12:00:00`), 'MM/dd/yy')
    : formatBusinessDateTimeShort(value);
}

export function PipelineLineReport({ loans, isLoading, dateRange, datePreset, selectedStatus: selectedStatusProp, onStatusChange, onDrillToActivity }: PipelineLineReportProps) {
  const [localSelectedStatus, setLocalSelectedStatus] = useState<PipelineStatus | 'all'>('all');
  const selectedStatus = selectedStatusProp ?? localSelectedStatus;
  const setSelectedStatus = onStatusChange ?? setLocalSelectedStatus;
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());

  // Effective date range boundaries (stable refs so downstream memos don't
  // churn on the per-render fallback Dates).
  const rangeFrom = useMemo(() => dateRange?.from ?? startOfMonth(new Date()), [dateRange?.from]);
  const rangeTo = useMemo(() => dateRange?.to ?? new Date(), [dateRange?.to]);

  // Pacific-pinned aggregation: chips, chart rows, and expandable details all
  // derive from one in-range event list (see pipelineLineModel).
  const model = useMemo(
    () => buildPipelineLineModel(loans, { from: rangeFrom, to: rangeTo }),
    [loans, rangeFrom, rangeTo]
  );
  const { granularity, rangeTotals, rows, detailsByKey } = model;

  const toggleDate = (dateKey: string) => {
    setExpandedDates((prev) => {
      const next = new Set(prev);
      if (next.has(dateKey)) {
        next.delete(dateKey);
      } else {
        next.add(dateKey);
      }
      return next;
    });
  };

  // Dynamic labels based on granularity and date range
  const chartTitle = useMemo(() => {
    if (granularity === 'daily') {
      if (datePreset === 'mtd') {
        return `Daily Activity - ${format(rangeFrom, 'MMMM yyyy')}`;
      }
      return `Daily Activity - ${format(rangeFrom, 'MMM d')} to ${format(rangeTo, 'MMM d, yyyy')}`;
    }
    const startLabel = format(rangeFrom, 'MMM yyyy');
    const endLabel = format(rangeTo, 'MMM yyyy');
    if (startLabel === endLabel) {
return `Monthly Activity - ${startLabel}`;
}
    return `Monthly Activity - ${startLabel} to ${endLabel}`;
  }, [granularity, datePreset, rangeFrom, rangeTo]);

  const breakdownTitle = granularity === 'daily' ? 'Daily Breakdown' : 'Monthly Breakdown';

  const visibleStatuses =
    selectedStatus === 'all'
      ? STATUS_LINE_CONFIG
      : STATUS_LINE_CONFIG.filter((s) => s.status === selectedStatus);

  // Chart keeps every bucket (continuity); the table hides empty buckets.
  const breakdownRows = rows.filter((row) => row.total > 0);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12" role="status" aria-label="Loading milestone flow">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        <span className="sr-only">Loading milestone flow</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Summary Chips */}
      <div className="flex flex-wrap gap-2">
        {STATUS_LINE_CONFIG.map(({ status, label, color }) => (
          <button
            key={status}
            type="button"
            className={`inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-sm transition-all hover:shadow-sm ${
              selectedStatus === status
                ? 'ring-2 ring-primary bg-primary/5 border-primary'
                : 'bg-card hover:bg-muted/50'
            }`}
            onClick={() => setSelectedStatus(selectedStatus === status ? 'all' : status)}
            aria-pressed={selectedStatus === status}
          >
            <span className="font-medium" style={{ color }}>{label}</span>
            <span className="font-bold tabular-nums" style={{ color }}>{rangeTotals[status].count}</span>
            <span className="text-muted-foreground tabular-nums">{formatCurrency(rangeTotals[status].amount)}</span>
          </button>
        ))}
      </div>

      {/* Line Chart */}
      <Card>
        <CardHeader>
          <CardTitle>{chartTitle}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[300px] sm:h-[340px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={rows} margin={{ top: 5, right: 18, left: 4, bottom: 5 }} accessibilityLayer>
                <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  tick={{ fontSize: 12 }}
                  tickLine={false}
                  axisLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px'
                  }}
                />
                <Legend />
                {visibleStatuses.map(({ status, label, color }) => (
                  <Line
                    key={status}
                    type="monotone"
                    dataKey={status}
                    name={label}
                    stroke={color}
                    strokeWidth={2}
                    dot={{ fill: color, strokeWidth: 2, r: 3 }}
                    activeDot={{ r: 5 }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* Breakdown Table */}
      <Card>
        <CardHeader>
          <CardTitle>{breakdownTitle}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <caption className="sr-only">Canonical milestone events by reporting period</caption>
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 px-3 font-medium">
                    {granularity === 'daily' ? 'Date' : 'Month'}
                  </th>
                  {STATUS_LINE_CONFIG.map(({ status, label, color }) => (
                    <th
                      key={status}
                      className="text-center py-2 px-3 font-medium"
                      style={{ color }}
                    >
                      {label}
                    </th>
                  ))}
                  <th className="text-center py-2 px-3 font-medium">Total</th>
                </tr>
              </thead>
              <tbody>
                {breakdownRows.map((row) => {
                  const dateKey = row.key;
                  const isExpanded = expandedDates.has(dateKey);
                  const details = (detailsByKey.get(dateKey) || []).filter(
                    (d) => selectedStatus === 'all' || d.status === selectedStatus
                  );

                  return (
                    <Fragment key={dateKey}>
                      <tr className="border-b hover:bg-muted/50">
                        <td className="py-2 px-3">
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                            onClick={() => toggleDate(dateKey)}
                            aria-expanded={isExpanded}
                          >
                            {isExpanded
                              ? <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                              : <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />}
                            {row.label}
                          </button>
                        </td>
                        {STATUS_LINE_CONFIG.map(({ status }) => {
                          const count = row[status];
                          return (
                            <td key={status} className="text-center py-2 px-3">
                              {count > 0 && onDrillToActivity ? (
                                <button
                                  type="button"
                                  className="tabular-nums underline-offset-2 hover:underline hover:text-primary"
                                  title="View in Activity"
                                  onClick={() => onDrillToActivity({ status })}
                                >
                                  {count}
                                </button>
                              ) : (
                                count || '-'
                              )}
                            </td>
                          );
                        })}
                        <td className="text-center py-2 px-3 font-medium">{row.total}</td>
                      </tr>
                      {isExpanded && details.length > 0 && (
                        <tr className="bg-muted/30">
                          <td colSpan={STATUS_LINE_CONFIG.length + 2} className="p-0">
                            <div className="px-6 py-3">
                              <table className="w-full text-xs">
                                <caption className="sr-only">Loans contributing milestone events for {row.label}</caption>
                                <thead>
                                  <tr className="border-b text-muted-foreground">
                                    <th className="text-left py-1.5 px-2 font-medium">Milestone</th>
                                    <th className="text-left py-1.5 px-2 font-medium">Loan #</th>
                                    <th className="text-left py-1.5 px-2 font-medium">Borrower</th>
                                    <th className="text-left py-1.5 px-2 font-medium">LO</th>
                                    <th className="text-right py-1.5 px-2 font-medium">Amount</th>
                                    <th className="text-left py-1.5 px-2 font-medium">Done By</th>
                                    <th className="text-right py-1.5 px-2 font-medium">Event date</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {details.map((d, idx) => (
                                    <tr key={`${d.loan.encompass_loan_id}-${d.status}-${idx}`} className="border-b last:border-0">
                                      <td className="py-1.5 px-2 font-medium" style={{ color: d.color }}>
                                        {d.label}
                                      </td>
                                      <td className="py-1.5 px-2">
                                        {d.loan.trans_details_loan_number || '-'}
                                      </td>
                                      <td className="py-1.5 px-2">
                                        {getBorrowerName(d.loan)}
                                      </td>
                                      <td className="py-1.5 px-2">
                                        {getLoanOfficerName(d.loan)}
                                      </td>
                                      <td className="py-1.5 px-2 text-right">
                                        {d.loan.trans_details_loan_amt
                                          ? formatCurrency(d.loan.trans_details_loan_amt)
                                          : '-'}
                                      </td>
                                      <td className="py-1.5 px-2">
                                        {d.doneBy || '-'}
                                      </td>
                                      <td className="py-1.5 px-2 text-right text-muted-foreground whitespace-nowrap">
                                        {formatEventDate(d.doneDate)}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
